// LivestreamAside.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import UniversalComponent from '../component/UniversalComponent.jsx';
import ModalTracking from '../component/ModalTracking.jsx';

import {
  sicBoHeight,
  gameMenuHeight,
  dragonTigerFightHeight,
  dragonTigerFightHeaderHeight,
  giftSlotHeight,
  giftSlotHeaderHeight,
} from '../style/variables.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import GameIconSource from '../../img/ic-game-white-full.svg';
import { ButtonId, ModalId } from '../resource/trackingConstants.js';
import { ASIDE_WIDTH } from '../resource/liveStreamConstants.js';
import { NULL_FUNCTION, EMPTY_ARRAY } from '../resource/defaults.js';
import { Game } from '../resource/gameConstants.js';

export const LivestreamAside = ({
  miniProfileModalId = '',
  miniBadgeInfoModalId = '',
  usingGameId = '',
  streamId = '',
  isGiftPanelOpen = false,
  isGameOpen = false,
  isGamePanelOpen = false,
  isMiniProfileModalOpen = false,
  isMiniBadgeInfoOpen = false,
  isSuperChatOpen = false,
  isShowInteractSection = true,
  needStreamTokenForExclusivePrivatePerforming = false,
  setStreamPanel = NULL_FUNCTION,
  closeGameSicBo = NULL_FUNCTION,
  shouldDisplayChatInput = false,
  games = EMPTY_ARRAY,
  giocoGames = EMPTY_ARRAY,
}) => {
  const isGameSicBoOpen = isGameOpen && usingGameId === Game.SIC_BO;
  const isGameGiocoOpen = isGameOpen && giocoGames.includes(usingGameId);
  const isGameGiftSlotOpen = isGameOpen && usingGameId === Game.GIFT_SLOT;
  const giocoMixpanelModalId =
    usingGameId === Game.SOARING_HIGH
      ? ModalId.LiveStream.SoaringHigh
      : usingGameId === Game.GOLDEN_GATE
        ? ModalId.LiveStream.GoldenGate
        : ModalId.LiveStream.DragonTigerFight;
  return (
    <StyledLivestreamAside>
      {isShowInteractSection && (
        <TopPanel>
          <UniversalComponent
            filename="Container_LiveStreamAsideTopPanel"
            streamId={streamId}
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/LiveStreamAsideTopPanel.js')
            }
          />
        </TopPanel>
      )}
      <PinnedWrapper>
        <UniversalComponent
          filename="Container_LivestreamPinnedMessages"
          streamId={streamId}
          importer={
            /* #__LOADABLE__ */ () =>
              import('../container/LivestreamPinnedMessages.js')
          }
        />
      </PinnedWrapper>
      <ChatroomWrapper>
        <UniversalComponent
          filename="Container_LiveStreamChatroom"
          streamId={streamId}
          importer={
            /* #__LOADABLE__ */ () =>
              import('../container/LiveStreamChatroom.js')
          }
        />
      </ChatroomWrapper>
      <MiniModalWrapper>
        {isMiniBadgeInfoOpen && (
          <ModalTracking
            id="MiniBadgeInfo"
            modalId={ModalId.LiveStream.MiniProfileBadgeDetail}
          >
            <UniversalComponent
              filename="Container_MiniBadgeInfo"
              modalId={miniBadgeInfoModalId}
              mixpanelModalId={ModalId.LiveStream.MiniProfileBadgeDetail}
              importer={
                /* #__LOADABLE__ */ () =>
                  import('../container/MiniBadgeInfo.js')
              }
            />
          </ModalTracking>
        )}
        {isMiniProfileModalOpen && (
          <ModalTracking
            id="MiniProfile"
            modalId={ModalId.LiveStream.LivestreamMiniProfile}
          >
            <UniversalComponent
              filename="Container_MiniProfile"
              modalId={miniProfileModalId}
              miniBadgeInfoModalId={miniBadgeInfoModalId}
              mixpanelModalId={ModalId.LiveStream.LivestreamMiniProfile}
              importer={
                /* #__LOADABLE__ */ () => import('../container/MiniProfile.js')
              }
            />
          </ModalTracking>
        )}
      </MiniModalWrapper>
      <ChatWrapper
        shouldDisplayChatInput={shouldDisplayChatInput}
        isSuperChatOpen={isSuperChatOpen}
      >
        {isSuperChatOpen && (
          <SuperChatPanel>
            <UniversalComponent
              filename="Container_LivestreamSuperChatPanel"
              streamId={streamId}
              importer={
                /* #__LOADABLE__ */ () =>
                  import('../container/LivestreamSuperChatPanel.js')
              }
            />
          </SuperChatPanel>
        )}
        <ChatInputWrapper>
          {shouldDisplayChatInput && (
            <UniversalComponent
              filename="Container_LiveStreamChatInput"
              streamId={streamId}
              importer={
                /* #__LOADABLE__ */ () =>
                  import('../container/LiveStreamChatInput.js')
              }
            />
          )}
          {!!games.length &&
            !needStreamTokenForExclusivePrivatePerforming &&
            isShowInteractSection && (
              <GamePanelButton
                isActive={isGamePanelOpen}
                data-element_id={ButtonId.LiveStream.ButtonGame}
                onClick={() => {
                  setStreamPanel({
                    isGamePanelOpen: !isGamePanelOpen,
                  });
                }}
              >
                <img src={GameIconSource} alt="Game" width="20" height="20" />
              </GamePanelButton>
            )}
        </ChatInputWrapper>
      </ChatWrapper>
      <BottomPanel
        maxHeight={
          isGiftPanelOpen
            ? 330
            : isGameSicBoOpen
              ? sicBoHeight
              : isGamePanelOpen
                ? gameMenuHeight
                : isGameGiocoOpen
                  ? dragonTigerFightHeight + dragonTigerFightHeaderHeight
                  : isGameGiftSlotOpen
                    ? giftSlotHeight + giftSlotHeaderHeight
                    : 0
        }
      >
        {isGameSicBoOpen ? (
          <ModalTracking id="GameSicBo" modalId={ModalId.LiveStream.SicBoGame}>
            <UniversalComponent
              filename="Container_GameSicBo"
              closeGameSicBo={closeGameSicBo}
              importer={
                /* #__LOADABLE__ */ () => import('../container/GameSicBo.js')
              }
            />
          </ModalTracking>
        ) : isGamePanelOpen ? (
          <GameMenuWrapper>
            <UniversalComponent
              filename="Container_GameMenu"
              streamId={streamId}
              importer={
                /* #__LOADABLE__ */ () => import('../container/GameMenu.js')
              }
            />
          </GameMenuWrapper>
        ) : isGameGiocoOpen ? (
          <ModalTracking id="GameGiocoGroup" modalId={giocoMixpanelModalId}>
            <UniversalComponent
              filename="Container_GameGiocoGroup"
              streamId={streamId}
              mixpanelModalId={giocoMixpanelModalId}
              importer={
                /* #__LOADABLE__ */ () =>
                  import('../container/GameGiocoGroup.js')
              }
            />
          </ModalTracking>
        ) : isGameGiftSlotOpen ? (
          <ModalTracking
            id="GameGiftSlot"
            modalId={ModalId.LiveStream.GiftSlot}
          >
            <UniversalComponent
              filename="Container_GameGiftSlot"
              streamId={streamId}
              importer={
                /* #__LOADABLE__ */ () => import('../container/GameGiftSlot.js')
              }
            />
          </ModalTracking>
        ) : null}
      </BottomPanel>
    </StyledLivestreamAside>
  );
};

LivestreamAside.propTypes = {
  streamId: PropTypes.string,
  miniProfileModalId: PropTypes.string,
  miniBadgeInfoModalId: PropTypes.string,
  usingGameId: PropTypes.string,
  isGiftPanelOpen: PropTypes.bool,
  isGameOpen: PropTypes.bool,
  isGamePanelOpen: PropTypes.bool,
  isMiniProfileModalOpen: PropTypes.bool,
  isMiniBadgeInfoOpen: PropTypes.bool,
  isSuperChatOpen: PropTypes.bool,
  isShowInteractSection: PropTypes.bool,
  needStreamTokenForExclusivePrivatePerforming: PropTypes.bool,
  setStreamPanel: PropTypes.func,
  closeGameSicBo: PropTypes.func,
  shouldDisplayChatInput: PropTypes.bool,
  games: PropTypes.array,
  giocoGames: PropTypes.array,
};

const StyledLivestreamAside = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-width: ${ASIDE_WIDTH}px;
  display: flex;
  flex-direction: column;
`;

const PinnedWrapper = styled.div`
  position: relative;
  padding: 8px 16px 0 16px;
`;

const ChatroomWrapper = styled.div`
  padding: 8px 16px;

  display: flex;
  min-height: 0;
  flex: 1;
`;

const MiniModalWrapper = styled.div``;

const SuperChatPanel = styled.div``;

const ChatWrapper = styled.div`
  position: relative;
  width: 100%;
  ${({ isSuperChatOpen }) =>
    isSuperChatOpen
      ? css`
          border-top-right-radius: 8px;
          border-top-left-radius: 8px;
          background: linear-gradient(
              90deg,
              rgba(0, 210, 193, 0.2) 0%,
              rgba(93, 130, 188, 0.2) 48.44%,
              rgba(142, 55, 215, 0.2) 100%
            ),
            #191919;
        `
      : css``};
  ${({ shouldDisplayChatInput }) =>
    shouldDisplayChatInput
      ? css`
          border-top: solid 1px rgba(100, 100, 100, 0.15);
        `
      : ''}
`;

const ChatInputWrapper = styled.div`
  padding: 12px 16px 8px;
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BottomPanelButton = styled.button`
  ${ResetButtonStyle};
  min-width: 40px;
  min-height: 40px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(35, 35, 35, 0.4);
  > img {
    width: 28px;
    height: 28px;
    object-fit: contain;
  }
`;

const GamePanelButton = styled(BottomPanelButton)`
  ${({ isActive }) =>
    isActive
      ? css`
          background-color: rgba(0, 210, 190, 0.4);
        `
      : css``}
  > img {
    width: 100%;
    height: 100%;
  }
`;

const BottomPanel = styled.div.attrs(({ maxHeight }) => ({
  style: {
    maxHeight: `${maxHeight}px`,
  },
}))`
  position: relative;
  width: 100%;
  transition: max-height 0.3s linear;
`;

const TopPanel = styled.div`
  position: relative;
  width: 100%;
  transition: max-height 0.3s linear;
`;

const GameMenuWrapper = styled.div`
  padding-left: 16px;
  padding-right: 16px;
`;

export default LivestreamAside;
